import React, { forwardRef, useEffect, useRef } from "react";
import { FlarePopup, type IFlarePopupController } from "flare-ui";
import "./popup.css";

export type PopupPlacement = "bottom" | "top" | "left" | "right";
export type PopupAlign = "start" | "center" | "end";

export interface PopupProps extends React.HTMLAttributes<HTMLDivElement> {
    open: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement | null>;
    placement?: PopupPlacement;
    align?: PopupAlign;
    offset?: number;
    offsetX?: number;
    offsetY?: number;
    xOffset?: number;
    yOffset?: number;
    matchAnchorWidth?: boolean;
    preferPlacement?: boolean;
    portalRoot?: HTMLElement | null;
    closeOnEscape?: boolean;
    closeOnOutsidePointerDown?: boolean;
    autoFocus?: boolean;
    trapFocus?: boolean;
    restoreFocus?: boolean;
    className?: string;
}

export const Popup = forwardRef<HTMLDivElement, PopupProps>(function PopupInner({
    open,
    onClose,
    anchorRef,
    placement = "bottom",
    align = "start",
    offset = 8,
    offsetX,
    offsetY,
    xOffset,
    yOffset,
    matchAnchorWidth = false,
    preferPlacement = false,
    portalRoot,
    closeOnEscape = true,
    closeOnOutsidePointerDown = true,
    autoFocus = false,
    trapFocus = false,
    restoreFocus = false,
    className,
    style,
    children,
    ...restProps
}, forwardedRef) {
    const popupControllerRef = useRef<IFlarePopupController>(null);
    const resolvedOffsetX = offsetX ?? xOffset ?? 0;
    const resolvedOffsetY = offsetY ?? yOffset ?? 0;

    useEffect(() => {
        const popupController = popupControllerRef.current;
        if (!popupController) return;

        if (!open) {
            popupController.close();
            return;
        }

        popupController.open({
            anchor: anchorRef.current ?? undefined,
            placement,
            align,
            offset,
            offsetX: resolvedOffsetX,
            offsetY: resolvedOffsetY,
            matchAnchorWidth,
            preferPlacement,
        });
    }, [align, anchorRef, matchAnchorWidth, offset, open, placement, preferPlacement, resolvedOffsetX, resolvedOffsetY]);

    return (
        <FlarePopup
            ref={popupControllerRef}
            elementRef={forwardedRef}
            className={[className, open ? "open" : null].filter(Boolean).join(" ") || undefined}
            style={style}
            zIndex={118}
            portalTarget={portalRoot ?? undefined}
            closeOnEscape={closeOnEscape}
            closeOnOutsidePointerDown={closeOnOutsidePointerDown}
            autoFocus={autoFocus}
            trapFocus={trapFocus}
            restoreFocus={restoreFocus}
            onRequestClose={() => {
                onClose();
                return true;
            }}
            data-popup-root="true"
            data-popup-open={open ? "true" : "false"}
            {...restProps}
        >
            {children}
        </FlarePopup>
    );
});
